import { Geometry } from './Geometry';
import Point from 'drawing/util/Point';
import * as THREE from 'three';

type LineData = {
    points: THREE.BufferGeometry;
    color: string;
    totalPoints: number;
};

const POINTS_PER_LINE = 100;

class ThreeBufferGeometry implements Geometry {
    private lineData: LineData[];
    private lastPoint: Point | null;

    private lines: THREE.Line[];

    private lineDataIndex: number;

    constructor() {
        this.lineData = [];
        this.lines = [];
        this.lastPoint = null;
        this.lineDataIndex = 0;
    }

    add(scene: THREE.Scene): void {
        this.lines = this.lineData.map(
            (ld) => new THREE.Line(ld.points, new THREE.LineBasicMaterial({ color: ld.color })),
        );
        this.lines.forEach((line) => scene.add(line));
    }

    clear(scene: THREE.Scene): void {
        this.lines.forEach((line) => scene.remove(line));
        this.lines = [];
        this.lineData = [];
        this.lineDataIndex = 0;
    }

    pushLine(color: string, connect: boolean): void {
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(POINTS_PER_LINE * 3); // 3 vertices per point
        geometry.addAttribute('position', new THREE.BufferAttribute(positions, 3));

        this.lineData.push({
            points: geometry,
            color,
            totalPoints: 0,
        });
        this.lineDataIndex = this.lineData.length - 1;

        if (connect && this.lastPoint) {
            this.pushPoint(this.lastPoint);
        }
    }

    pushPoint(point: Point): void {
        this.lastPoint = point;
        let lineData = this.lineData[this.lineData.length - 1];
        if (lineData.totalPoints === POINTS_PER_LINE) {
            this.pushLine(lineData.color, true);
            lineData = this.lineData[this.lineData.length - 1];
        }

        const positions = lineData.points.attributes.position.array;
        const index = lineData.totalPoints * 3;

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore: we can too set this
        positions[index] = point.getX();
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore: we can too set this
        positions[index + 1] = point.getY();
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore: we can too set this
        positions[index + 2] = point.getZ();

        lineData.totalPoints += 1;
        lineData.points.setDrawRange(0, lineData.totalPoints);
    }

    getLineColor(index: number): string | null {
        return this.lineData?.[index]?.color;
    }

    private _setLineDataIndex(newIndex: number): void {
        if (this.lineData[newIndex]) {
            this.lineDataIndex = newIndex;
        }
    }

    forward(): void {
        const lineData = this.lineData[this.lineDataIndex];
        const count = lineData.points.drawRange.count + 1;
        lineData.points.setDrawRange(0, count);

        const endOfSection = count === lineData.totalPoints;
        if (endOfSection) {
            this._setLineDataIndex(this.lineDataIndex + 1);
        }
    }

    backward(): void {
        const lineData = this.lineData[this.lineDataIndex];
        const count = lineData.points.drawRange.count - 1;
        lineData.points.setDrawRange(0, count);

        const beginningOfSection = count === 0;
        if (beginningOfSection) {
            this._setLineDataIndex(this.lineDataIndex - 1);
        }
    }
}

export default ThreeBufferGeometry;
